import React, { useState } from "react";
import { StyleSheet, View } from "react-native";
import { colors, layout } from "@/constants";
import Interactive from "./Interactive";
import Text from "./Text";
import ScrollView from "./ScrollView";
import Icon from "./Icon";

interface Option {
  id: string;
  label: string;
}

interface DropdownProps {
  label?: string;
  options: Option[];
  selectedId: string;
  onSelect: (id: string) => void;
  placeholder?: string;
}

/**
 * A dropdown component for selecting from a list of options.
 * Displays selected value and expands to show all options when clicked.
 */
export function Dropdown({
  options,
  selectedId,
  onSelect,
  placeholder = "Select an option"
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((opt) => opt.id === selectedId);

  const handleSelect = (id: string) => {
    onSelect(id);
    setIsOpen(false);
  };

  return (
    <View style={styles.container}>
      <Interactive style={styles.button} onPress={() => setIsOpen(!isOpen)}>
        {selectedOption ? (
          <Text variant="semiBold">{selectedOption.label}</Text>
        ) : (
          <Text color="secondary">{placeholder}</Text>
        )}
        <Icon
          name={isOpen ? "arrow_drop_up" : "arrow_drop_down"}
          size="large"
          color="primary"
          style={{ color: colors.primary }}
        />
      </Interactive>

      {isOpen && (
        <View style={styles.dropdown}>
          <ScrollView style={styles.scrollView} contentContainerStyle={false}>
            {options.map((item) => (
              <Interactive
                key={item.id}
                style={[
                  styles.option,
                  item.id === selectedId && styles.selectedOption
                ]}
                onPress={() => handleSelect(item.id)}
              >
                <Text variant={item.id === selectedId ? "semiBold" : "regular"}>
                  {item.label}
                </Text>
                {item.id === selectedId && (
                  <Icon
                    name="check"
                    size="small"
                    style={{ color: colors.primary }}
                  />
                )}
              </Interactive>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%"
  },
  label: {
    marginBottom: layout.space.xs
  },
  button: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: colors.surface,
    borderWidth: layout.borderWidth.thin,
    borderColor: colors.primary,
    borderRadius: layout.borderRadius.large,
    paddingHorizontal: layout.space.md,
    paddingVertical: layout.space.md
  },
  dropdown: {
    marginTop: layout.space.xs,
    backgroundColor: colors.surface,
    borderRadius: layout.borderRadius.large,
    borderWidth: layout.borderWidth.thin,
    borderColor: colors.primary,
    maxHeight: 200,
    overflow: "hidden"
  },
  scrollView: {
    paddingVertical: layout.space.xs
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: layout.space.md,
    paddingVertical: layout.space.md,
    marginHorizontal: layout.space.xs,
    borderRadius: layout.borderRadius.small
  },
  selectedOption: {
    backgroundColor: colors.primaryContainer
  }
});
